import {
  buildAssetClassGroupView,
  buildPortfolioAllocationView,
} from '@components/PortfolioAllocationGrid/buildPortfolioAllocationView';
import { isAssetClassAllocationModel } from '@ovComponents/4-module/widgets/portfolio/components/buildCustomAllocationInput';
import { isTradeableAllocationMember } from '@ovComponents/4-module/widgets/portfolio/components/tradeableAllocationMembers';
import { fractionToDisplayPercent } from '@utils/allocationUnits';
import {
  accountColumnLabel,
  accountColumnLabelLines,
} from './accountColumnLabel';
import { accountAllocationRows } from './buildAccountAllocationSlots';
import { cellFraction } from './placeholderMatrix';
import type {
  AccountAllocationGridAccount,
  AccountAllocationGridReport,
} from './types';

export type BuildCsvTemplateOptions = {
  t: (key: string) => string;
  translateBackendString: (translatedObject: unknown) => string;
};

// Excel and Sheets run text starting with =, +, - or @ as a formula.
const FORMULA_PREFIX = /^[=+\-@]/;

const PERCENT_DECIMAL_PLACES = 2;

const quoteCsvField = (value: string) =>
  /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

const textCsvField = (value: string) =>
  quoteCsvField(FORMULA_PREFIX.test(value) ? `'${value}` : value);

const percentCsvField = (fraction: number | undefined) =>
  fractionToDisplayPercent(fraction ?? 0, PERCENT_DECIMAL_PLACES).toFixed(
    PERCENT_DECIMAL_PLACES,
  );

// The template's columns and rows, in the grid's order. Upload reads it too, so
// columns that share an account number pair up in the order they were written.
export const csvTemplateLayout = (
  report: AccountAllocationGridReport,
  { t, translateBackendString }: BuildCsvTemplateOptions,
) => {
  const isAssetClassPortfolio = isAssetClassAllocationModel(report);
  const view = buildPortfolioAllocationView({
    portfolioReport: report,
    t,
    translateBackendString,
  });

  const accountsBySubAccountId = new Map(
    report.subAccounts.map((account) => [account.subAccount.id, account]),
  );
  const accounts = view.subAccounts
    .filter(isTradeableAllocationMember)
    .map((subAccount) => accountsBySubAccountId.get(subAccount.id))
    .filter(
      (account): account is AccountAllocationGridAccount =>
        account !== undefined,
    );

  const securities = accountAllocationRows(view.securities);
  const assetClassGroups = buildAssetClassGroupView({
    securities,
    isAssetClassPortfolio,
    t,
    translateBackendString,
  });

  return {
    isAssetClassPortfolio,
    accounts,
    securities: assetClassGroups?.flatMap((group) => group.items) ?? securities,
  };
};

export const buildAccountAllocationCsvTemplate = (
  report: AccountAllocationGridReport,
  options: BuildCsvTemplateOptions,
): string => {
  const { t } = options;
  const { isAssetClassPortfolio, accounts, securities } = csvTemplateLayout(
    report,
    options,
  );
  const rows = accounts.length > 0 ? securities : [];

  const header = [
    textCsvField(
      t('components:accountAllocationGrid.csvTemplate.securityColumn'),
    ),
    textCsvField(
      t('components:accountAllocationGrid.csvTemplate.fundIdColumn'),
    ),
    ...accounts.map((account) =>
      textCsvField(
        accountColumnLabelLines(
          accountColumnLabel(account.subAccount.account, t),
        ).join('\n'),
      ),
    ),
  ].join(',');

  const body = rows.map((security) =>
    [
      textCsvField(security.ticker || security.translatedName),
      quoteCsvField(security.id),
      ...accounts.map((account) =>
        percentCsvField(
          cellFraction(
            account,
            {
              expectedPercentage: security.expectedPercentage,
              financialProduct: { id: security.id },
            },
            isAssetClassPortfolio,
          ),
        ),
      ),
    ].join(','),
  );

  return [header, ...body].join('\n');
};
